import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';

/**
 * TodoService - CRUD for personal todos scoped to a user within a school.
 *
 * Todos are stored at schools/{schoolId}/todos/{todoId}.
 * Each todo belongs to a single user (userId field). Only the owner can access their todos.
 */
class TodoService {
  constructor() {
    this.cache = new Map();       // `${schoolId}-${userId}` -> todos
    this.timestamps = new Map();  // same key -> timestamp
    this.ttl = 60 * 1000;        // 1 minute
  }

  _cacheKey(schoolId, userId) {
    return `${schoolId}-${userId}`;
  }

  _collectionRef(schoolId) {
    return collection(db, 'schools', schoolId, 'todos');
  }

  /**
   * Get all todos for a user.
   */
  async getTodosForUser(schoolId, userId, forceRefresh = false) {
    if (!schoolId) throw new Error('schoolId is required');
    if (!userId) throw new Error('userId is required');

    const key = this._cacheKey(schoolId, userId);
    const now = Date.now();

    if (!forceRefresh && this.cache.has(key) && (now - this.timestamps.get(key)) < this.ttl) {
      return this.cache.get(key);
    }

    try {
      const q = query(
        this._collectionRef(schoolId),
        where('userId', '==', userId)
      );

      const snap = await getDocs(q);
      const todos = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          // Incomplete first, then by createdAt descending
          if (a.completed !== b.completed) return a.completed ? 1 : -1;
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });

      this.cache.set(key, todos);
      this.timestamps.set(key, now);

      return todos;
    } catch (error) {
      console.error('[TodoService] Error fetching todos:', error);
      throw error;
    }
  }

  /**
   * Create a new todo.
   */
  async createTodo(schoolId, todoData, userId) {
    if (!schoolId) throw new Error('schoolId is required');
    if (!userId) throw new Error('userId is required');
    if (!todoData.title?.trim()) throw new Error('Todo title is required');

    const toTimestamp = (val) => {
      if (!val) return null;
      if (val instanceof Timestamp) return val;
      if (val instanceof Date) return Timestamp.fromDate(val);
      return Timestamp.fromDate(new Date(val));
    };

    const todoDoc = {
      title: todoData.title.trim(),
      description: todoData.description?.trim() || '',
      dueDate: toTimestamp(todoData.dueDate),
      completed: false,
      completedAt: null,
      userId,
      relatedStudentId: todoData.relatedStudentId || null,
      relatedTaskId: todoData.relatedTaskId || null,
      relatedWorkflowInstanceId: todoData.relatedWorkflowInstanceId || null,
      relatedWorkflowStepId: todoData.relatedWorkflowStepId || null,
      schoolId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      const ref = await addDoc(this._collectionRef(schoolId), todoDoc);
      this._invalidateCache(schoolId, userId);
      return { id: ref.id, ...todoDoc };
    } catch (error) {
      console.error('[TodoService] Error creating todo:', error);
      throw error;
    }
  }

  /**
   * Toggle a todo's completed state.
   */
  async toggleTodo(schoolId, todoId, completed, userId) {
    if (!schoolId || !todoId) throw new Error('schoolId and todoId are required');

    const data = {
      completed,
      completedAt: completed ? serverTimestamp() : null,
      updatedAt: serverTimestamp(),
    };

    try {
      const docRef = doc(db, 'schools', schoolId, 'todos', todoId);
      await updateDoc(docRef, data);
      this._invalidateCache(schoolId, userId);
      return { id: todoId, ...data };
    } catch (error) {
      console.error('[TodoService] Error toggling todo:', error);
      throw error;
    }
  }

  /**
   * Update a todo (partial update).
   */
  async updateTodo(schoolId, todoId, updates, userId) {
    if (!schoolId || !todoId) throw new Error('schoolId and todoId are required');

    const toTimestamp = (val) => {
      if (!val) return null;
      if (val instanceof Timestamp) return val;
      if (val instanceof Date) return Timestamp.fromDate(val);
      return Timestamp.fromDate(new Date(val));
    };

    const data = { ...updates, updatedAt: serverTimestamp() };
    if ('dueDate' in data) data.dueDate = toTimestamp(data.dueDate);
    // Don't allow overriding userId or schoolId
    delete data.userId;
    delete data.schoolId;

    try {
      const docRef = doc(db, 'schools', schoolId, 'todos', todoId);
      await updateDoc(docRef, data);
      this._invalidateCache(schoolId, userId);
      return { id: todoId, ...data };
    } catch (error) {
      console.error('[TodoService] Error updating todo:', error);
      throw error;
    }
  }

  /**
   * Delete a todo (hard delete).
   */
  async deleteTodo(schoolId, todoId, userId) {
    if (!schoolId || !todoId) throw new Error('schoolId and todoId are required');

    try {
      const docRef = doc(db, 'schools', schoolId, 'todos', todoId);
      await deleteDoc(docRef);
      this._invalidateCache(schoolId, userId);
    } catch (error) {
      console.error('[TodoService] Error deleting todo:', error);
      throw error;
    }
  }

  /**
   * Get todos with due dates in a date range (for calendar integration - Phase 3).
   */
  async getTodosForDateRange(schoolId, userId, startDate, endDate) {
    const todos = await this.getTodosForUser(schoolId, userId);
    const start = startDate instanceof Date ? startDate.getTime() : startDate;
    const end = endDate instanceof Date ? endDate.getTime() : endDate;

    return todos.filter(todo => {
      if (!todo.dueDate) return false;
      const dueMs = todo.dueDate?.toMillis?.() || new Date(todo.dueDate).getTime();
      return dueMs >= start && dueMs <= end;
    });
  }

  _invalidateCache(schoolId, userId) {
    if (userId) {
      const key = this._cacheKey(schoolId, userId);
      this.cache.delete(key);
      this.timestamps.delete(key);
    } else {
      // Clear all cache entries for this school
      for (const key of this.cache.keys()) {
        if (key.startsWith(schoolId)) {
          this.cache.delete(key);
          this.timestamps.delete(key);
        }
      }
    }
  }
}

export const todoService = new TodoService();
export default todoService;
